import { LocalDate } from '@js-joda/core';
import type { QueryClient } from '@tanstack/query-core';
import { queryOptions } from '@tanstack/react-query';
import type { SecurityMasterService } from 'src/services/SecurityMasterService';
import { SecurityMasterLoadError } from 'src/services/SecurityMasterService/SecurityMasterLoadError';
import type { TokenState } from 'src/store/reducers/auth';

const FIVE_MINUTES = 1000 * 60 * 5;

/**
 * Namespace prefix for every query answered from the security master.
 * Invalidated as a unit via `invalidateSecurityMasterQueries`.
 */
const SECURITY_MASTER_QUERY_PREFIX = 'SECURITY_MASTER';

/**
 * Refetch every mounted query answered from the security master.
 * `SecurityMasterServiceImpl` calls this whenever the worker reports that the
 * master's content changed, so cached lookups follow the new master.
 */
export function invalidateSecurityMasterQueries(queryClient: QueryClient): Promise<void> {
    return queryClient.invalidateQueries({ queryKey: [SECURITY_MASTER_QUERY_PREFIX] });
}

/**
 * Retry policy for the master download. The security-master worker performs
 * the fetch and classifies failures: transient ones (network, 5xx/429, empty
 * or capped master, truncated stream, contract drift) surface here as
 * `SecurityMasterLoadError` and retry with the standard backoff. Anything
 * else is an unexpected bug and must surface as a query error rather than
 * retry silently; the refetch interval still re-attempts it.
 */
export function shouldRetrySecurityMasterLoad(_failureCount: number, error: Error): boolean {
    return error instanceof SecurityMasterLoadError;
}

/**
 * Downloads the security master and keeps it fresh on a 5-minute refresh.
 * Owned and permanently observed by `SecurityMasterServiceImpl`; UI code
 * should not mount this query.
 */
export const getSecurityMasterLoadQuery = (
    accessToken: TokenState | null | undefined,
    securityMasterService: SecurityMasterService,
) =>
    queryOptions({
        queryFn: () => securityMasterService.fetchSecurityMaster(accessToken!.token, LocalDate.now(), []),
        queryKey: ['security-master-load'],
        refetchInterval: FIVE_MINUTES,
        enabled: Boolean(accessToken?.token),
        retry: shouldRetrySecurityMasterLoad,
        // The master is identical for every user and tier; skip the
        // QueryCacheUserService identity/entitlement resets (they would refetch
        // and briefly blank globally-shared data on login and masq switches).
        meta: { shared: true },
    });

export const getSecurityMasterEntryQuery = (securityMasterService: SecurityMasterService, symbol: string | undefined) =>
    queryOptions({
        queryFn: () => securityMasterService.fetchSecurityMasterEntry(symbol!),
        queryKey: [SECURITY_MASTER_QUERY_PREFIX, 'ENTRY', symbol!],
        enabled: Boolean(symbol),
        meta: { shared: true },
    });

export const getSecurityMasterEntriesQuery = (securityMasterService: SecurityMasterService, symbols: string[]) =>
    queryOptions({
        queryFn: () => securityMasterService.fetchSecurityMasterEntries(symbols).then((x) => Object.values(x)),
        queryKey: [SECURITY_MASTER_QUERY_PREFIX, 'BULK_ENTRY', ...symbols],
        enabled: symbols.length > 0,
        meta: { shared: true },
    });

export const getSymbolsMatchingPrefixQuery = (securityMasterService: SecurityMasterService, prefix: string) =>
    queryOptions({
        queryFn: () => securityMasterService.fetchSymbolsMatchingPrefix(prefix),
        queryKey: [SECURITY_MASTER_QUERY_PREFIX, 'PREFIX', prefix],
        enabled: Boolean(prefix),
        meta: { shared: true },
    });

export const getRandomSymbolsQuery = (securityMasterService: SecurityMasterService, count: number) =>
    queryOptions({
        queryFn: () => securityMasterService.fetchRandomSymbols(count),
        queryKey: [SECURITY_MASTER_QUERY_PREFIX, 'RANDOM', count],
        enabled: count > 0,
        meta: { shared: true },
    });
